import { ref, computed } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import {
    GetCompareOverview,
    type ApiGetCompareOverviewRequest,
    type CompareResultList,
    type CompareTextLang,
} from '@/infrastructure/api-functions/core-search-api/core-search-api';
import { create } from '@/utils/deferred';
import _ from 'lodash';
import sleep from '@/utils/sleep';
import { RavenInsertMeta } from 'library-@/services/sentry';

export const useCompareInit = createGlobalState(() => {
    const {
        leftId,
        rightId,
        isTargetEmpty,
    } = useCompareUrlState();

    // const deferred = ref(isSourceCustom.value ? undefined : create());
    const deferred = ref(create());

    const isStartInit = ref(false);
    const isLoading = ref(false);
    const isNodata = ref(false);
    const isTimeout = ref(false);

    const isSame = computed(() => {
        return !isTargetEmpty.value && leftId.value === rightId.value;
    });

    const compareResultList = ref<CompareResultList[]>();
    const compareLang = ref('');
    const textCompareLang = ref<CompareTextLang>();
    const srcPatentId = ref('');
    const srcReplaceId = ref('');
    const srcTextTrans = ref(false);
    const tgtPatentId = ref('');
    const tgtTextTrans = ref(false);
    const tgtReplaceId = ref('');
    const tgtPN = ref('');

    // 给compare-patent接口用
    const compareInitParams = ref<ApiGetCompareOverviewRequest | undefined>();

    const resetCompareInitState = () => {
        compareResultList.value = [];
        compareLang.value = '';
        textCompareLang.value = undefined;
        srcPatentId.value = '';
        srcReplaceId.value = '';
        srcTextTrans.value = false;
        tgtPatentId.value = '';
        tgtTextTrans.value = false;
        tgtReplaceId.value = '';
        tgtPN.value = '';
        compareInitParams.value = undefined;
    };

    const performFakeComparison = async (timer:number = 200) => {
        isStartInit.value = true;
        await sleep(timer);
        isStartInit.value = false;
    };

    const compareInit = async (params:ApiGetCompareOverviewRequest) => {
        if (!deferred.value || deferred.value?.completed) deferred.value = create();
        isStartInit.value = true;
        isLoading.value = true;
        isNodata.value = false;
        isTimeout.value = false;
        try {
            compareInitParams.value = params;
            const service = injectService<GetCompareOverview>(GetCompareOverview);
            const data = await service.getCompareOverview(params);
            RavenInsertMeta('view', 'Execute Patent Comparison');

            compareResultList.value = _.get(data, 'compare_with', []);
            const basicInfo = data.basic_info;
            // 这两个字段以接口返回为准
            compareLang.value = basicInfo.compare_lang;
            textCompareLang.value = basicInfo.text_compare_lang;

            // leftId和rightId可以替代
            // 这些字段有的话就取原本的，没有的话就取上一次的结果，每次切换专利的时候会重置掉
            srcPatentId.value = basicInfo.src_patent_id || srcPatentId.value;
            srcReplaceId.value = basicInfo.src_replace_id || srcReplaceId.value;
            srcTextTrans.value = basicInfo.src_text_translation || srcTextTrans.value;
            tgtPatentId.value = basicInfo.target_patent_id || tgtPatentId.value;
            tgtTextTrans.value = basicInfo.target_text_translation || tgtTextTrans.value;
            tgtReplaceId.value = basicInfo.target_replace_id || tgtReplaceId.value;
            tgtPN.value = basicInfo.target_pn || tgtPN.value;

            if (!compareResultList.value.length) isNodata.value = true;

            isLoading.value = false;
            deferred.value.resolve({});
            isStartInit.value = false;
        } catch (err) {
            deferred.value.resolve({});
            // @ts-ignore
            const errorCode = err._error_code as number;
            if (errorCode === 11164048) {
                // 记录超时状态，提示文案有区别
                isTimeout.value = true;
            }
            isNodata.value = true;
            isLoading.value = false;
            isStartInit.value = false;
        }
    };

    const matchTextList = computed(() => {
        return _.map(compareResultList.value, item => {
            return {
                text: item.src.similar_part,
                partId: item.paragraph_id,
            };
        });
    });

    return  {
        isLoading,
        isNodata,
        isTimeout,
        isSame,
        deferred,
        isStartInit,

        compareLang,
        textCompareLang,

        compareResultList,
        matchTextList,
        tgtPN,

        compareInitParams,

        compareInit,
        resetCompareInitState,
        performFakeComparison,
    };
});
